import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Trash2, 
  GripVertical, 
  Plus, 
  Image as ImageIcon, 
  Link as LinkIcon,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Upload,
  ExternalLink,
  Navigation
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * NavigationCardsEditor - Éditeur CMS pour le bloc Navigation Cards
 * 
 * VERSION: 2.0 - REFACTORED (null-based value system)
 * 
 * FONCTIONNALITÉS:
 * ✅ CRUD complet des cartes de navigation
 * ✅ Validation en temps réel via backend
 * ✅ Drag & Drop pour réordonner
 * ✅ Upload d'images + URL
 * ✅ Validation des routes internes uniquement
 * ✅ Preview live des cartes
 * 
 * SYSTÈME DE VALEURS:
 * - Stockage: null pour les valeurs vides (icon, color)
 * - UI Select: "__EMPTY__" comme valeur technique interne (jamais exposée)
 * - Compatibilité: "none" | "" | null | undefined → normalisé en null
 * 
 * CONTRAINTES BACKEND:
 * - link = routes internes uniquement
 * - is_visible obligatoire
 * - order obligatoire
 * - image optionnelle mais validée si fournie
 */

// ============================================================================
// HELPERS DE NORMALISATION (Système standardisé)
// ============================================================================

/**
 * Valeur technique interne pour Radix Select (jamais stockée/exposée)
 * Radix Select crash avec value="" donc on utilise une valeur spéciale
 */
const EMPTY_VALUE = '__EMPTY__';

/**
 * Normalise une valeur vers null (pour stockage)
 * Compatible avec les données existantes: "none" | "" | null | undefined
 */
const normalizeValue = (value) => {
  if (value === EMPTY_VALUE || value === 'none' || value === '' || value == null) {
    return null;
  }
  return value;
};

/**
 * Convertit une valeur pour utilisation dans Radix Select
 * null/undefined/""/none → EMPTY_VALUE (pour éviter crash)
 * autre valeur → valeur elle-même
 */
const safeSelectValue = (value) => {
  if (value === 'none' || value === '' || value == null) {
    return EMPTY_VALUE;
  }
  return value;
};

/**
 * Convertit la valeur Select vers valeur de stockage
 * EMPTY_VALUE → null
 * autre → valeur elle-même
 */
const fromSelectValue = (value) => {
  return value === EMPTY_VALUE ? null : value;
};

// ============================================================================
// CONSTANTES
// ============================================================================

// Icônes Lucide disponibles pour les cartes
const AVAILABLE_ICONS = [
  { value: EMPTY_VALUE, label: 'Aucune', isEmpty: true },
  { value: 'Clock', label: 'Horloge' },
  { value: 'Calendar', label: 'Calendrier' },
  { value: 'Tag', label: 'Étiquette' },
  { value: 'Star', label: 'Étoile' },
  { value: 'Gift', label: 'Cadeau' },
  { value: 'Package', label: 'Colis' },
  { value: 'Sparkles', label: 'Éclats' },
  { value: 'TrendingUp', label: 'Tendance' },
  { value: 'Zap', label: 'Éclair' },
  { value: 'Heart', label: 'Cœur' },
  { value: 'ShoppingCart', label: 'Panier' },
  { value: 'Users', label: 'Utilisateurs' },
  { value: 'Store', label: 'Magasin' },
  { value: 'Gamepad2', label: 'Jeux' },
  { value: 'Dice5', label: 'Dés' },
];

// Couleurs prédéfinies pour les cartes
const PRESET_COLORS = [
  { value: EMPTY_VALUE, label: 'Par défaut', preview: 'bg-gray-100', isEmpty: true },
  { value: 'bg-green-500/10 text-green-600 border-green-500/20', label: 'Vert', preview: 'bg-green-500/30' },
  { value: 'bg-blue-500/10 text-blue-600 border-blue-500/20', label: 'Bleu', preview: 'bg-blue-500/30' },
  { value: 'bg-purple-500/10 text-purple-600 border-purple-500/20', label: 'Violet', preview: 'bg-purple-500/30' },
  { value: 'bg-orange-500/10 text-orange-600 border-orange-500/20', label: 'Orange', preview: 'bg-orange-500/30' },
  { value: 'bg-red-500/10 text-red-600 border-red-500/20', label: 'Rouge', preview: 'bg-red-500/30' },
  { value: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20', label: 'Jaune', preview: 'bg-yellow-500/30' },
  { value: 'bg-pink-500/10 text-pink-600 border-pink-500/20', label: 'Rose', preview: 'bg-pink-500/30' },
  { value: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20', label: 'Indigo', preview: 'bg-indigo-500/30' },
  { value: 'bg-[#EBA79A]/15 text-[#D97B66] border-[#EBA79A]/25', label: 'Terracotta', preview: 'bg-[#EBA79A]/40' },
  { value: 'bg-[#D97B66]/15 text-[#D97B66] border-[#D97B66]/25', label: 'Brique', preview: 'bg-[#D97B66]/40' },
  { value: 'bg-[#E6B877]/15 text-[#C49A55] border-[#E6B877]/25', label: 'Doré', preview: 'bg-[#E6B877]/40' },
  { value: 'bg-[#A7B7C9]/15 text-[#6B8299] border-[#A7B7C9]/25', label: 'Ardoise', preview: 'bg-[#A7B7C9]/40' },
  { value: 'bg-[#B9B0C9]/15 text-[#8A7DA6] border-[#B9B0C9]/25', label: 'Lavande', preview: 'bg-[#B9B0C9]/40' },
  { value: 'bg-[#A9B8A8]/15 text-[#6E8A6D] border-[#A9B8A8]/25', label: 'Sauge', preview: 'bg-[#A9B8A8]/40' },
];

const NavigationCardsEditor = ({ content, onChange }) => {
  const { 
    title = '',
    cards = []
  } = content || {};

  // États locaux
  const [allowedRoutes, setAllowedRoutes] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [imageUploadMode, setImageUploadMode] = useState({}); // { cardId: 'url' | 'upload' }

  // Charger le schéma avec les routes autorisées
  useEffect(() => {
    loadSchema();
  }, []);

  const loadSchema = async () => {
    try {
      const response = await api.get('/blocks/navigation-cards-schema');
      setAllowedRoutes(response.data.allowed_routes || []);
    } catch (error) {
      console.error('[NavigationCardsEditor] Erreur chargement schéma:', error);
      // Fallback avec routes par défaut
      setAllowedRoutes([
        '/dashboard/nouveautes',
        '/dashboard/precommandes',
        '/dashboard/offres',
        '/dashboard',
        '/'
      ]);
    }
  };

  // Validation en temps réel via backend
  const validateContent = useCallback(async (contentToValidate) => {
    if (!contentToValidate?.cards?.length) {
      setValidationErrors([]);
      return true;
    }

    setIsValidating(true);
    try {
      const response = await api.post('/blocks/validate-navigation-cards', {
        content: contentToValidate
      });
      
      if (response.data.valid) {
        setValidationErrors([]);
        return true;
      } else {
        setValidationErrors(response.data.errors || []);
        return false;
      }
    } catch (error) {
      console.error('[NavigationCardsEditor] Erreur validation:', error);
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.errors) {
        setValidationErrors(errorDetail.errors);
      } else {
        setValidationErrors(['Erreur de validation']);
      }
      return false;
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Debounce validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (cards.length > 0) {
        validateContent({ title, cards });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [title, cards, validateContent]);

  // Mettre à jour le titre de section
  const handleTitleChange = (newTitle) => {
    onChange({
      ...content,
      title: newTitle
    });
  };

  // Ajouter une nouvelle carte
  const handleAddCard = () => {
    const newCard = {
      id: `nav-card-${Date.now()}`,
      title: '',
      description: '',
      link: '',
      image: '',
      icon: '',
      color: '',
      cta_label: '',
      is_visible: true,
      order: cards.length
    };
    
    onChange({
      ...content,
      cards: [...cards, newCard]
    });

    // Ouvrir automatiquement la nouvelle carte
    setExpandedCards(prev => ({
      ...prev,
      [newCard.id]: true
    }));
  };

  // Supprimer une carte
  const handleDeleteCard = (cardId) => {
    if (!window.confirm('Supprimer cette carte de navigation ?')) return;
    
    const updatedCards = cards
      .filter(c => c.id !== cardId)
      .map((c, idx) => ({ ...c, order: idx })); // Recalculer l'ordre
    
    onChange({
      ...content,
      cards: updatedCards
    });
    
    toast.success('Carte supprimée');
  };

  // Mettre à jour une carte
  const handleUpdateCard = (cardId, field, value) => {
    onChange({
      ...content,
      cards: cards.map(c => {
        if (c.id !== cardId) return c;
        return { ...c, [field]: value };
      })
    });
  };

  // Toggle visibilité d'une carte
  const handleToggleVisibility = (cardId) => {
    const card = cards.find(c => c.id === cardId);
    if (card) {
      handleUpdateCard(cardId, 'is_visible', !card.is_visible);
    }
  };

  // Toggle expansion d'une carte
  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  // Upload d'image
  const handleImageUpload = async (cardId, file) => {
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.url) {
        handleUpdateCard(cardId, 'image', response.data.url);
        setImageUploadMode(prev => ({ ...prev, [cardId]: 'upload' }));
        toast.success('Image uploadée');
      }
    } catch (error) {
      console.error('[NavigationCardsEditor] Erreur upload:', error);
      toast.error('Erreur lors de l\'upload de l\'image');
    }
  };

  // Drag & Drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(cards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Mettre à jour l'ordre
    const reorderedCards = items.map((card, index) => ({
      ...card,
      order: index
    }));

    onChange({
      ...content,
      cards: reorderedCards
    });
  };

  // Vérifier si un lien est valide
  const isValidLink = (link) => {
    if (!link) return false;
    return link.startsWith('/');
  };

  // Render d'une carte individuelle
  const renderCardEditor = (card, index) => {
    const isExpanded = expandedCards[card.id] ?? false;
    const hasErrors = validationErrors.some(err => err.includes(`Carte [${index}]`));
    const uploadMode = imageUploadMode[card.id] || (card.image?.startsWith('/api/') ? 'upload' : 'url');

    return (
      <Draggable key={card.id} draggableId={card.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`
              border rounded-lg mb-3 transition-all
              ${snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}
              ${hasErrors ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white'}
              ${!card.is_visible ? 'opacity-60' : ''}
            `}
          >
            {/* Header de la carte (toujours visible) */}
            <div 
              className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50/50 rounded-t-lg"
              onClick={() => toggleCardExpansion(card.id)}
            >
              <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                <GripVertical className="w-4 h-4 text-gray-400" />
              </div>
              
              <div className="flex-1 flex items-center gap-2 min-w-0">
                <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>
                <span className="font-medium truncate">
                  {card.title || 'Sans titre'}
                </span>
                {card.link && (
                  <Badge variant="outline" className="text-xs truncate max-w-[150px]">
                    {card.link}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2">
                {hasErrors && (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleVisibility(card.id);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                  title={card.is_visible ? 'Masquer' : 'Afficher'}
                >
                  {card.is_visible ? (
                    <Eye className="w-4 h-4 text-green-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCard(card.id);
                  }}
                  className="p-1 hover:bg-red-100 rounded text-red-500"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>

            {/* Contenu éditeur (collapsible) */}
            {isExpanded && (
              <div className="p-4 pt-0 border-t border-gray-100 space-y-4">
                {/* Titre */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium">
                    Titre <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={card.title || ''}
                    onChange={(e) => handleUpdateCard(card.id, 'title', e.target.value)}
                    placeholder="Ex: Nouveautés en cours"
                    className={!card.title ? 'border-red-300' : ''}
                  />
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Description</Label>
                  <Textarea
                    value={card.description || ''}
                    onChange={(e) => handleUpdateCard(card.id, 'description', e.target.value)}
                    placeholder="Description courte (optionnel)"
                    rows={2}
                    className="resize-none"
                  />
                </div>

                {/* Lien (route interne) */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium">
                    Lien (route interne) <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={card.link || ''}
                      onChange={(e) => handleUpdateCard(card.id, 'link', e.target.value)}
                      placeholder="/dashboard/nouveautes"
                      className={`pl-9 ${!isValidLink(card.link) && card.link ? 'border-red-300' : ''}`}
                    />
                  </div>
                  {allowedRoutes.length > 0 && (
                    <Select
                      value={card.link || undefined}
                      onValueChange={(value) => handleUpdateCard(card.id, 'link', value)}
                    >
                      <SelectTrigger className="text-xs h-8 mt-1">
                        <SelectValue placeholder="Choisir une route..." />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedRoutes.map((route) => (
                          <SelectItem key={route} value={route} className="text-xs">
                            {route}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {card.link && !isValidLink(card.link) && (
                    <p className="text-xs text-red-500 mt-1">
                      Le lien doit commencer par /
                    </p>
                  )}
                </div>

                {/* Image */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Image (optionnel)</Label>
                  
                  {/* Toggle URL / Upload */}
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setImageUploadMode(prev => ({ ...prev, [card.id]: 'url' }))}
                      className={`text-xs px-3 py-1 rounded ${uploadMode === 'url' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageUploadMode(prev => ({ ...prev, [card.id]: 'upload' }))}
                      className={`text-xs px-3 py-1 rounded ${uploadMode === 'upload' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      Upload
                    </button>
                  </div>

                  {uploadMode === 'url' ? (
                    <Input
                      value={card.image || ''}
                      onChange={(e) => handleUpdateCard(card.id, 'image', e.target.value)}
                      placeholder="https://... ou /api/uploads/..."
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <label className="flex-1 flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                        <Upload className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">Choisir une image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(card.id, e.target.files?.[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}

                  {/* Preview image */}
                  {card.image && (
                    <div className="mt-2 relative w-24 h-24 rounded overflow-hidden border">
                      <img
                        src={card.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${card.image}` : card.image}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <button
                        type="button"
                        onClick={() => handleUpdateCard(card.id, 'image', '')}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Icône et Couleur (Grid 2 cols) */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Icône */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Icône</Label>
                    <Select
                      value={safeSelectValue(card.icon)}
                      onValueChange={(value) => handleUpdateCard(card.id, 'icon', fromSelectValue(value))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_ICONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Couleur */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Couleur</Label>
                    <Select
                      value={safeSelectValue(card.color)}
                      onValueChange={(value) => handleUpdateCard(card.id, 'color', fromSelectValue(value))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PRESET_COLORS.map(({ value, label, preview }) => (
                          <SelectItem key={value} value={value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded ${preview}`} />
                              {label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* CTA Label */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Libellé du bouton (optionnel)</Label>
                  <Input
                    value={card.cta_label || ''}
                    onChange={(e) => handleUpdateCard(card.id, 'cta_label', e.target.value)}
                    placeholder="Ex: Découvrir"
                  />
                </div>

                {/* Preview Card */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs font-medium text-gray-500 mb-2 block">
                    Aperçu
                  </Label>
                  <div className={`p-4 rounded-lg border ${card.color || 'bg-white'}`}>
                    <div className="font-medium">{card.title || 'Titre...'}</div>
                    {card.description && (
                      <p className="text-sm text-gray-600 mt-1">{card.description}</p>
                    )}
                    {card.link && (
                      <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        {card.link}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold">Navigation Cards</h3>
        </div>
        
        {/* Status validation */}
        {isValidating ? (
          <Badge variant="outline" className="text-gray-500">
            Validation...
          </Badge>
        ) : validationErrors.length > 0 ? (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {validationErrors.length} erreur(s)
          </Badge>
        ) : cards.length > 0 ? (
          <Badge variant="outline" className="text-green-600 border-green-300">
            <CheckCircle className="w-3 h-3 mr-1" />
            Valide
          </Badge>
        ) : null}
      </div>

      {/* Titre de section */}
      <div className="space-y-1">
        <Label className="text-xs font-medium">Titre de la section (optionnel)</Label>
        <Input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Ex: Explorer les Nouveautés"
        />
      </div>

      {/* Erreurs de validation */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-2">
            <AlertCircle className="w-4 h-4" />
            Erreurs de validation
          </div>
          <ul className="text-sm text-red-600 space-y-1">
            {validationErrors.map((error, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Liste des cartes avec Drag & Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="navigation-cards">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="min-h-[100px]"
            >
              {cards.length === 0 ? (
                <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-lg">
                  <Navigation className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune carte de navigation</p>
                  <p className="text-xs mt-1">Cliquez sur "Ajouter" pour commencer</p>
                </div>
              ) : (
                cards.map((card, index) => renderCardEditor(card, index))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Bouton ajouter */}
      <Button
        type="button"
        variant="outline"
        onClick={handleAddCard}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" />
        Ajouter une carte
      </Button>

      {/* Info fallback */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        <strong>💡 Info :</strong> Si aucune carte visible n'est définie, le système utilisera automatiquement 
        la configuration par défaut (pageConfig.js).
      </div>
    </div>
  );
};

export default NavigationCardsEditor;
